import { useEffect, useState } from "react";
import {
  DndContext, closestCenter, PointerSensor, useSensor, useSensors,
} from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { checklistsApi } from "../services/checklistsApi";
import { Card } from "../components/common/Card";
import { Button } from "../components/common/Button";
import { Badge } from "../components/common/Badge";
import { useToast } from "../context/ToastContext";
import { Field } from "../components/common/Field";
import { HivePicker } from "../components/common/HivePicker";
import { SitePicker } from "../components/common/SitePicker";

function SortableItem({ id, text, onRemove }) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id });
  const style = { transform: CSS.Transform.toString(transform), transition };
  return (
    <li ref={setNodeRef} style={{ ...style, display: "flex", alignItems: "center", gap: "var(--space-2)", padding: "var(--space-2)", background: "var(--color-bg-subtle)", borderRadius: "var(--radius-md)", marginBottom: "var(--space-1)" }}>
      <span {...attributes} {...listeners} style={{ cursor: "grab" }} aria-label="Drag to reorder">⠿</span>
      <span style={{ flex: 1 }}>{text}</span>
      <Button size="sm" variant="ghost" onClick={onRemove} aria-label={`Remove ${text}`}>✕</Button>
    </li>
  );
}

/**
 * Inspection checklist templates and their per-target defaults (#212, #228).
 *
 * Everything on this page used to live in localStorage, including a
 * hardcoded copy of the four starter templates. Both are gone: templates
 * come from the API, and the starters have exactly one home in
 * api/services/checklists.py.
 *
 * The assignment control is the substance of #212. It was a text box
 * labelled "Site or Hive ID" whose value became the key of a flat object,
 * so site 3 and hive 3 overwrote each other. It is now a type toggle plus
 * the matching picker: the user chooses "North Orchard" or "North Orchard
 * - Hive 3" and never sees, or needs to know, either number.
 */
export function InspectionChecklistsPage() {
  const { notify } = useToast();
  const [templates, setTemplates] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [failed, setFailed] = useState(false);

  const [editing, setEditing] = useState(null); // { template_id | null, name }
  const [items, setItems] = useState([]);
  const [newItem, setNewItem] = useState("");
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);

  const [targetType, setTargetType] = useState("Hive");
  const [targetId, setTargetId] = useState(null);

  const sensors = useSensors(useSensor(PointerSensor));

  async function load() {
    try {
      const [t, a] = await Promise.all([
        checklistsApi.listTemplates(),
        checklistsApi.listAssignments(),
      ]);
      setTemplates(t.templates || []);
      setAssignments(a.assignments || []);
      setFailed(false);
    } catch {
      setFailed(true);
    }
  }
  useEffect(() => { load(); }, []);

  function startEditing(template) {
    setEditing(template || { template_id: null, name: "" });
    setItems(template ? [...template.items] : []);
    setName(template ? template.name : "");
  }

  function handleDragEnd(event) {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setItems((prev) => arrayMove(prev, prev.indexOf(active.id), prev.indexOf(over.id)));
    }
  }

  async function saveTemplate() {
    if (!name.trim()) return notify("Give the checklist a name", "warning");
    setSaving(true);
    try {
      const saved = await checklistsApi.saveTemplate({
        template_id: editing.template_id || undefined,
        name: name.trim(),
        items,
      });
      notify(`"${saved.name}" saved`, "success");
      // Re-read rather than patching local state: the server assigns the
      // id on create, and the next save must be an update rather than a
      // second template with the same name.
      await load();
      setEditing({ template_id: saved.template_id, name: saved.name });
    } catch (err) {
      notify(err.apiMessage || "Couldn't save that checklist", "danger");
    } finally {
      setSaving(false);
    }
  }

  async function retireTemplate() {
    if (!editing?.template_id) return;
    try {
      await checklistsApi.retireTemplate(editing.template_id);
      notify(`"${editing.name}" removed`, "success");
      setEditing(null);
      setItems([]);
      await load();
    } catch (err) {
      notify(err.apiMessage || "Couldn't remove that checklist", "danger");
    }
  }

  async function assign() {
    if (!editing?.template_id) return notify("Save the checklist first", "warning");
    if (!targetId) return notify(`Choose a ${targetType.toLowerCase()}`, "warning");
    try {
      const r = await checklistsApi.assign(targetType, targetId, editing.template_id);
      notify(`"${r.template_name}" is now the default there`, "success");
      setTargetId(null);
      await load();
    } catch (err) {
      notify(err.apiMessage || "Couldn't set that default", "danger");
    }
  }

  async function clearAssignment(a) {
    try {
      await checklistsApi.unassign(a.target_type, a.target_id);
      await load();
    } catch (err) {
      notify(err.apiMessage || "Couldn't clear that default", "danger");
    }
  }

  if (failed) {
    return (
      <div>
        <h1>Inspection Checklists</h1>
        <Card>
          <p>Couldn't load your checklists. Refresh to try again.</p>
          <Button onClick={load}>Try again</Button>
        </Card>
      </div>
    );
  }

  return (
    <div>
      <h1>Inspection Checklists</h1>

      <div style={{ display: "grid", gridTemplateColumns: "260px 1fr", gap: "var(--space-4)" }}>
        <Card title="Your checklists">
          <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
            {(templates || []).map((t) => (
              <li key={t.template_id}>
                <Button
                  variant={editing?.template_id === t.template_id ? "primary" : "ghost"}
                  onClick={() => startEditing(t)}
                  style={{ width: "100%", justifyContent: "flex-start", marginBottom: 4 }}
                >
                  {t.name}
                </Button>
              </li>
            ))}
          </ul>
          {templates && templates.length === 0 && (
            <p style={{ color: "var(--color-text-muted)", margin: 0 }}>
              No checklists yet. Create one to get started.
            </p>
          )}
          <Button size="sm" variant="secondary" onClick={() => startEditing(null)} style={{ marginTop: "var(--space-3)" }}>
            + New Checklist
          </Button>
        </Card>

        <Card title={editing ? (editing.name || "New checklist") : "Select or create a checklist"}>
          {!editing ? (
            <p style={{ color: "var(--color-text-muted)" }}>Choose a checklist on the left, or create a new one.</p>
          ) : (
            <div>
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={items} strategy={verticalListSortingStrategy}>
                  <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
                    {items.map((item) => (
                      <SortableItem key={item} id={item} text={item} onRemove={() => setItems((prev) => prev.filter((i) => i !== item))} />
                    ))}
                  </ul>
                </SortableContext>
              </DndContext>

              <div style={{ display: "flex", gap: "var(--space-2)", marginTop: "var(--space-3)", alignItems: "flex-end" }}>
                <div style={{ flex: 1 }}>
                  <Field label="New checklist item">
                    {(id, aria) => (
                      <input id={id} {...aria} value={newItem}
                        onChange={(e) => setNewItem(e.target.value)}
                        onKeyDown={(e) => {
                          // Enter adds and keeps focus. Building a list of
                          // fifteen items should not mean fifteen trips to
                          // a button with the mouse.
                          if (e.key === "Enter" && newItem.trim()) {
                            e.preventDefault();
                            setItems((prev) => [...prev, newItem.trim()]);
                            setNewItem("");
                          }
                        }}
                        className="touch-target" />
                    )}
                  </Field>
                </div>
                {/* Secondary (#327). Adding a line to the checklist and
                    saving the checklist were the same weight, and only
                    one of them is what the editor is for -- the other is
                    something you do five times on the way there. */}
                <Button size="sm" variant="secondary" onClick={() => { if (newItem.trim()) { setItems((prev) => [...prev, newItem.trim()]); setNewItem(""); } }}>Add</Button>
              </div>

              <div style={{ marginTop: "var(--space-4)", display: "flex", gap: "var(--space-2)", flexWrap: "wrap", alignItems: "flex-end" }}>
                <Field label="Checklist name">
                  {(id, aria) => <input id={id} {...aria} value={name} onChange={(e) => setName(e.target.value)} className="touch-target" />}
                </Field>
                <Button onClick={saveTemplate} disabled={saving}>{saving ? "Saving…" : "Save"}</Button>
                {editing.template_id && (
                  <Button variant="ghost" onClick={retireTemplate}>Remove</Button>
                )}
              </div>

              {/* #212. Was a text box labelled "Site or Hive ID" whose
                  value became the key -- so site 3 and hive 3 were the
                  same entry and silently overwrote each other. The type is
                  now an explicit choice and the id comes from a picker. */}
              <div style={{ marginTop: "var(--space-5)", borderTop: "1px solid var(--color-border)", paddingTop: "var(--space-4)" }}>
                <h3 style={{ marginTop: 0 }}>Use this by default</h3>
                <p style={{ color: "var(--color-text-muted)", marginTop: 0 }}>
                  Pick a whole site, or one hive. A hive's own default wins over its site's.
                </p>
                <div style={{ display: "flex", gap: "var(--space-2)", flexWrap: "wrap", alignItems: "flex-end" }}>
                  <Field label="Apply to">
                    {(id, aria) => (
                      <select id={id} {...aria} value={targetType}
                        onChange={(e) => { setTargetType(e.target.value); setTargetId(null); }}
                        className="touch-target">
                        <option value="Hive">One hive</option>
                        <option value="Site">A whole site</option>
                      </select>
                    )}
                  </Field>
                  {targetType === "Hive"
                    ? <HivePicker label="Which hive" value={targetId} onChange={setTargetId} />
                    : <SitePicker label="Which site" value={targetId} onChange={setTargetId} />}
                  <Button variant="secondary" onClick={assign}>Set as default</Button>
                </div>
              </div>
            </div>
          )}
        </Card>
      </div>

      <Card title="Defaults you've set" className="section-spacing">
        {assignments.length === 0 ? (
          <p style={{ margin: 0, color: "var(--color-text-muted)" }}>
            No defaults yet. Setting one means a hive opens straight into the
            right checklist instead of asking every time.
          </p>
        ) : (
          <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "grid", gap: "var(--space-2)" }}>
            {assignments.map((a) => (
              <li key={`${a.target_type}-${a.target_id}`}
                style={{ display: "flex", alignItems: "center", gap: "var(--space-2)", flexWrap: "wrap" }}>
                {/* The type is shown, not inferred from the label. Two
                    targets can share a name; they can no longer share an
                    assignment. */}
                <Badge tone={a.target_type === "Hive" ? "info" : "neutral"}>{a.target_type}</Badge>
                <strong>{a.target_label}</strong>
                <span style={{ color: "var(--color-text-muted)" }}>uses</span>
                <span>{a.template_name}</span>
                <Button size="sm" variant="ghost" onClick={() => clearAssignment(a)}
                  aria-label={`Clear the default for ${a.target_label}`}>Clear</Button>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}
